import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { locationTagsKeys } from './queries/useLocationTags';
import { useNetworkStatus } from './queries/useOfflineRequestsQuery';
import { requestsKeys } from './queries/useRequestsQuery';
import { userProfileKeys } from './useUserProfile';
import { syncService, SyncResult } from '../services/syncService';
import { showToast } from '../utils/toast';

/** The app-wide offline toast (softened for #1069; see the call site). */
export const OFFLINE_TOAST_TEXT =
  "You're offline. Some features are unavailable.";

export const useOfflineToast = () => {
  const isOnline = useNetworkStatus();
  const queryClient = useQueryClient();
  const [isToastDismissed, setIsToastDismissed] = useState(false);
  const offlineToastId = useRef<string | null>(null);
  const dismissalTimer = useRef<NodeJS.Timeout | null>(null);
  const wasOffline = useRef(false);

  useEffect(() => {
    // When going offline, show the toast (unless user previously dismissed it)
    if (!isOnline && !isToastDismissed && !offlineToastId.current) {
      // ⚠️ "SOME FEATURES ARE UNAVAILABLE", NOT "UPDATES WILL SYNC" (#1069
      // offline state, 2026-09-25). This toast is app-wide, and messaging is
      // deliberately online-only (no queue, no replay), so the old promise
      // was false for it. Site-wide copy change: name it in the MR.
      offlineToastId.current = showToast.offline(OFFLINE_TOAST_TEXT);
      wasOffline.current = true;

      // Set up auto-dismissal after 10 seconds
      dismissalTimer.current = setTimeout(() => {
        setIsToastDismissed(true);
        offlineToastId.current = null;
      }, 10000);
    }

    // When coming back online, dismiss the toast and reset dismissal state
    if (isOnline) {
      if (offlineToastId.current) {
        showToast.dismiss(offlineToastId.current);
        offlineToastId.current = null;
      }
      if (dismissalTimer.current) {
        clearTimeout(dismissalTimer.current);
        dismissalTimer.current = null;
      }
      setIsToastDismissed(false); // Reset dismissal state when back online
    }
  }, [isOnline, isToastDismissed]);

  // Listen for sync completion and show notification
  useEffect(() => {
    const handleSyncComplete = (result: SyncResult) => {
      // Invalidate profile query if profile was synced so UI updates without manual refresh
      if (result.profileSynced) {
        queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      }

      if ((result.locationTagOperationsSynced ?? 0) > 0) {
        queryClient.invalidateQueries({ queryKey: locationTagsKeys.all });
        for (const requestId of result.locationTagRequestIdsSynced ?? []) {
          queryClient.invalidateQueries({
            queryKey: requestsKeys.detail(requestId),
          });
        }
      }

      if (result.locationTagNameConflicts?.length) {
        const names = result.locationTagNameConflicts.join(', ');
        showToast.error(
          `Could not sync location tag name${
            result.locationTagNameConflicts.length > 1 ? 's' : ''
          }: ${names}. Rename and try again.`,
          { id: 'offline-location-tag-conflict' },
        );
      }

      // Shared stable id so the sync-result toast collapses into one even though
      // useOfflineToast is mounted (and listening) in several components at once.
      const syncResultToastId = { id: 'offline-sync-result' };

      // Only show sync toast if we were previously offline and something was synced
      if (wasOffline.current && result.syncedCount > 0) {
        if (result.failedCount === 0) {
          showToast.success(
            `Synced ${result.syncedCount} offline update${
              result.syncedCount > 1 ? 's' : ''
            }`,
            syncResultToastId,
          );
        } else {
          showToast.info(
            `Synced ${result.syncedCount} update${
              result.syncedCount > 1 ? 's' : ''
            }, ${result.failedCount} failed`,
            syncResultToastId,
          );
        }
        wasOffline.current = false;
      } else if (wasOffline.current && result.failedCount > 0) {
        showToast.error(
          `Failed to sync ${result.failedCount} offline update${
            result.failedCount > 1 ? 's' : ''
          }`,
          syncResultToastId,
        );
        wasOffline.current = false;
      }
    };

    const removeListener = syncService.addSyncListener(handleSyncComplete);
    return removeListener;
  }, [queryClient]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dismissalTimer.current) {
        clearTimeout(dismissalTimer.current);
      }
    };
  }, []);

  const dismissOfflineToast = () => {
    if (offlineToastId.current) {
      showToast.dismiss(offlineToastId.current);
      offlineToastId.current = null;
    }
    if (dismissalTimer.current) {
      clearTimeout(dismissalTimer.current);
      dismissalTimer.current = null;
    }
    setIsToastDismissed(true);
  };

  return {
    isOnline,
    dismissOfflineToast,
  };
};
